import {
  createUseReadContract,
  createUseWriteContract,
  createUseSimulateContract,
  createUseWatchContractEvent,
} from 'wagmi/codegen'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// BitMillions
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const bitMillionsAbi = [
  {
    type: 'constructor',
    inputs: [
      { name: 'vrfCoordinator', internalType: 'address', type: 'address' },
      { name: 'usdcAddress', internalType: 'address', type: 'address' },
      { name: '_DEV_TESTING', internalType: 'bool', type: 'bool' },
    ],
    stateMutability: 'nonpayable',
  },
  { type: 'error', inputs: [], name: 'CannotApplyDiscountAndUseReferral' },
  { type: 'error', inputs: [], name: 'CannotReferYourself' },
  {
    type: 'error',
    inputs: [
      { name: 'charityAddress', internalType: 'address', type: 'address' },
    ],
    name: 'CharityAlreadyAdded',
  },
  {
    type: 'error',
    inputs: [
      { name: 'charityAddress', internalType: 'address', type: 'address' },
    ],
    name: 'CharityNotAdded',
  },
  {
    type: 'error',
    inputs: [{ name: 'errorId', internalType: 'uint256', type: 'uint256' }],
    name: 'InvalidInputs',
  },
  {
    type: 'error',
    inputs: [
      {
        name: 'ticket',
        internalType: 'struct BitMillions.Ticket',
        type: 'tuple',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
    ],
    name: 'InvalidTicket',
  },
  {
    type: 'error',
    inputs: [
      {
        name: 'current',
        internalType: 'enum BitMillions.LotteryState',
        type: 'uint8',
      },
    ],
    name: 'LotteryNotOpen',
  },
  {
    type: 'error',
    inputs: [
      { name: 'current', internalType: 'uint256', type: 'uint256' },
      { name: 'required', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'NotEnoughPoints',
  },
  { type: 'error', inputs: [], name: 'NothingToClaim' },
  {
    type: 'error',
    inputs: [
      { name: 'have', internalType: 'address', type: 'address' },
      { name: 'want', internalType: 'address', type: 'address' },
    ],
    name: 'OnlyCoordinatorCanFulfill',
  },
  {
    type: 'error',
    inputs: [
      { name: 'have', internalType: 'address', type: 'address' },
      { name: 'owner', internalType: 'address', type: 'address' },
      { name: 'coordinator', internalType: 'address', type: 'address' },
    ],
    name: 'OnlyOwnerOrCoordinator',
  },
  {
    type: 'error',
    inputs: [
      { name: 'given', internalType: 'uint256', type: 'uint256' },
      { name: 'expected', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'RequestIdMismatch',
  },
  {
    type: 'error',
    inputs: [{ name: 'token', internalType: 'address', type: 'address' }],
    name: 'SafeERC20FailedOperation',
  },
  {
    type: 'error',
    inputs: [
      { name: 'total', internalType: 'uint256', type: 'uint256' },
      { name: 'limit', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'TicketsBuyLimitExceeded',
  },
  {
    type: 'error',
    inputs: [
      { name: 'total', internalType: 'uint256', type: 'uint256' },
      { name: 'limit', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'TicketsTotalLimitExceeded',
  },
  { type: 'error', inputs: [], name: 'UpkeepNotNeeded' },
  {
    type: 'error',
    inputs: [
      { name: 'given', internalType: 'uint256', type: 'uint256' },
      { name: 'current', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'WrongDrawNumber',
  },
  { type: 'error', inputs: [], name: 'ZeroAddress' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'charityAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'charityInfo',
        internalType: 'struct BitMillions.CharityInfo',
        type: 'tuple',
        components: [
          { name: 'name', internalType: 'string', type: 'string' },
          { name: 'website', internalType: 'string', type: 'string' },
        ],
        indexed: false,
      },
    ],
    name: 'CharityAdded',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'charityAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'charityInfo',
        internalType: 'struct BitMillions.CharityInfo',
        type: 'tuple',
        components: [
          { name: 'name', internalType: 'string', type: 'string' },
          { name: 'website', internalType: 'string', type: 'string' },
        ],
        indexed: false,
      },
    ],
    name: 'CharityInfoUpdated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'charityAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'CharityRemoved',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'vrfCoordinator',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
    ],
    name: 'CoordinatorSet',
  },
  { type: 'event', anonymous: false, inputs: [], name: 'Deployed' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'playerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'discount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'DiscountApplied',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'charityAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'donations',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'DonationsClaimed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'ticket',
        internalType: 'struct BitMillions.Ticket',
        type: 'tuple',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
        indexed: false,
      },
      {
        name: 'tierWinners',
        internalType: 'struct BitMillions.TierWinners[]',
        type: 'tuple[]',
        components: [
          {
            name: 'tier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'winners', internalType: 'uint256', type: 'uint256' },
        ],
        indexed: false,
      },
    ],
    name: 'DrawClosed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'charityAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'jackpot',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
      {
        name: 'config',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
        indexed: false,
      },
    ],
    name: 'DrawOpened',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
    ],
    name: 'DrawPostponed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'playerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'drawPrizes',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'DrawPrizesCalculated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'proceeds',
        internalType: 'struct BitMillions.Proceeds',
        type: 'tuple',
        components: [
          { name: 'prizes', internalType: 'uint256', type: 'uint256' },
          { name: 'donations', internalType: 'uint256', type: 'uint256' },
          { name: 'royalties', internalType: 'uint256', type: 'uint256' },
        ],
        indexed: false,
      },
    ],
    name: 'DrawUpdated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'sender',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'FundsReceived',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'lastUpkeep',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'LastUpkeepUpdated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'fromState',
        internalType: 'enum BitMillions.LotteryState',
        type: 'uint8',
        indexed: false,
      },
      {
        name: 'toState',
        internalType: 'enum BitMillions.LotteryState',
        type: 'uint8',
        indexed: false,
      },
    ],
    name: 'LotteryStateChanged',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'nextDrawConfig',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
        indexed: false,
      },
    ],
    name: 'NextDrawConfigSet',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'from', internalType: 'address', type: 'address', indexed: true },
      { name: 'to', internalType: 'address', type: 'address', indexed: true },
    ],
    name: 'OwnershipTransferRequested',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'from', internalType: 'address', type: 'address', indexed: true },
      { name: 'to', internalType: 'address', type: 'address', indexed: true },
    ],
    name: 'OwnershipTransferred',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'playerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'referrerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'points',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'PlayerReferred',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'playerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'prizes',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'PrizesClaimed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'ownerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'royalties',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'RoyaltiesClaimed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'drawNumber',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'playerAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'tickets',
        internalType: 'struct BitMillions.Ticket[]',
        type: 'tuple[]',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
        indexed: false,
      },
    ],
    name: 'TicketsBought',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'upkeepConfig',
        internalType: 'struct BitMillions.UpkeepConfig',
        type: 'tuple',
        components: [
          { name: 'period', internalType: 'uint256', type: 'uint256' },
          { name: 'triggers', internalType: 'uint256[]', type: 'uint256[]' },
        ],
        indexed: false,
      },
    ],
    name: 'UpkeepConfigSet',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'vrfConfig',
        internalType: 'struct BitMillions.VRFConfig',
        type: 'tuple',
        components: [
          { name: 'subId', internalType: 'uint256', type: 'uint256' },
          { name: 'keyHash', internalType: 'bytes32', type: 'bytes32' },
          { name: 'callbackGasLimit', internalType: 'uint32', type: 'uint32' },
          {
            name: 'requestConfirmations',
            internalType: 'uint16',
            type: 'uint16',
          },
        ],
        indexed: false,
      },
    ],
    name: 'VRFConfigSet',
  },
  {
    type: 'function',
    inputs: [],
    name: 'DEV_DESTROY',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'DEV_RANDOM',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'DEV_RANDOM_', internalType: 'bool', type: 'bool' }],
    name: 'DEV_SET_RANDOM',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'DEV_TESTING',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'randomWords', internalType: 'uint256[]', type: 'uint256[]' },
    ],
    name: 'DEV_TESTING_fulfillRandomWords',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'LUCKY_STARS_JACKPOT_MARGIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'LUCKY_STARS_POOL_MIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'MAIN_NUMBERS_JACKPOT_MARGIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'MAIN_NUMBERS_POOL_MIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'PROCEEDS_ALLOCATION_DONATIONS_MAX',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'PROCEEDS_ALLOCATION_DONATIONS_MIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'PROCEEDS_ALLOCATION_PRIZES_MIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'PROCEEDS_ALLOCATION_ROLLOVER_MIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'PROCEEDS_ALLOCATION_ROYALTIES_MAX',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'TIERS_MIN',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'TIMELOCK',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'USDC',
    outputs: [{ name: '', internalType: 'contract IERC20', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'acceptOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'charityAddress', internalType: 'address', type: 'address' },
      {
        name: 'charityInfo',
        internalType: 'struct BitMillions.CharityInfo',
        type: 'tuple',
        components: [
          { name: 'name', internalType: 'string', type: 'string' },
          { name: 'website', internalType: 'string', type: 'string' },
        ],
      },
    ],
    name: 'addCharity',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'drawNumber', internalType: 'uint256', type: 'uint256' },
      {
        name: 'tickets',
        internalType: 'struct BitMillions.Ticket[]',
        type: 'tuple[]',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
      { name: 'referrerAddress', internalType: 'address', type: 'address' },
      { name: 'applyDiscount', internalType: 'bool', type: 'bool' },
    ],
    name: 'buyTickets',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: '', internalType: 'bytes', type: 'bytes' }],
    name: 'checkUpkeep',
    outputs: [
      { name: 'upkeepNeeded', internalType: 'bool', type: 'bool' },
      { name: '', internalType: 'bytes', type: 'bytes' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'claimDonations',
    outputs: [{ name: 'donations', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'claimPrizes',
    outputs: [{ name: 'prizes', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'claimRoyalties',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getCurrentDraw',
    outputs: [
      {
        name: 'drawConfig',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'jackpot', internalType: 'uint256', type: 'uint256' },
      {
        name: 'charity',
        internalType: 'struct BitMillions.Charity',
        type: 'tuple',
        components: [
          { name: 'address_', internalType: 'address', type: 'address' },
          {
            name: 'unclaimedDonations',
            internalType: 'uint256',
            type: 'uint256',
          },
          {
            name: 'info',
            internalType: 'struct BitMillions.CharityInfo',
            type: 'tuple',
            components: [
              { name: 'name', internalType: 'string', type: 'string' },
              { name: 'website', internalType: 'string', type: 'string' },
            ],
          },
          {
            name: 'node',
            internalType: 'struct BitMillions.Node',
            type: 'tuple',
            components: [
              { name: 'active', internalType: 'bool', type: 'bool' },
              { name: 'index', internalType: 'uint256', type: 'uint256' },
              { name: 'next', internalType: 'address', type: 'address' },
              { name: 'previous', internalType: 'address', type: 'address' },
            ],
          },
        ],
      },
      {
        name: 'proceeds',
        internalType: 'struct BitMillions.Proceeds',
        type: 'tuple',
        components: [
          { name: 'prizes', internalType: 'uint256', type: 'uint256' },
          { name: 'donations', internalType: 'uint256', type: 'uint256' },
          { name: 'royalties', internalType: 'uint256', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getLottery',
    outputs: [
      {
        name: 'nextDrawConfig',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
      {
        name: 'upkeepConfig',
        internalType: 'struct BitMillions.UpkeepConfig',
        type: 'tuple',
        components: [
          { name: 'period', internalType: 'uint256', type: 'uint256' },
          { name: 'triggers', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
      {
        name: 'vrfConfig',
        internalType: 'struct BitMillions.VRFConfig',
        type: 'tuple',
        components: [
          { name: 'subId', internalType: 'uint256', type: 'uint256' },
          { name: 'keyHash', internalType: 'bytes32', type: 'bytes32' },
          { name: 'callbackGasLimit', internalType: 'uint32', type: 'uint32' },
          {
            name: 'requestConfirmations',
            internalType: 'uint16',
            type: 'uint16',
          },
        ],
      },
      {
        name: 'lotteryState',
        internalType: 'enum BitMillions.LotteryState',
        type: 'uint8',
      },
      { name: 'lastUpkeep', internalType: 'uint256', type: 'uint256' },
      { name: 'drawNumber', internalType: 'uint256', type: 'uint256' },
      {
        name: 'drawConfig',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'jackpot', internalType: 'uint256', type: 'uint256' },
      { name: 'charityAddress', internalType: 'address', type: 'address' },
      {
        name: 'proceeds',
        internalType: 'struct BitMillions.Proceeds',
        type: 'tuple',
        components: [
          { name: 'prizes', internalType: 'uint256', type: 'uint256' },
          { name: 'donations', internalType: 'uint256', type: 'uint256' },
          { name: 'royalties', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'timestamp', internalType: 'uint256', type: 'uint256' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getNextDrawConfig',
    outputs: [
      {
        name: '',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'playerAddress', internalType: 'address', type: 'address' },
    ],
    name: 'getPlayer',
    outputs: [
      {
        name: 'player',
        internalType: 'struct BitMillions.Player',
        type: 'tuple',
        components: [
          { name: 'unclaimedPrizes', internalType: 'uint256', type: 'uint256' },
          { name: 'points', internalType: 'uint256', type: 'uint256' },
          { name: 'lastDrawUpdate', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'lastDrawPrize', internalType: 'uint256', type: 'uint256' },
      {
        name: 'tickets',
        internalType: 'struct BitMillions.Ticket[]',
        type: 'tuple[]',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getPreviousDraw',
    outputs: [
      {
        name: 'drawConfig',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'jackpot', internalType: 'uint256', type: 'uint256' },
      {
        name: 'charity',
        internalType: 'struct BitMillions.Charity',
        type: 'tuple',
        components: [
          { name: 'address_', internalType: 'address', type: 'address' },
          {
            name: 'unclaimedDonations',
            internalType: 'uint256',
            type: 'uint256',
          },
          {
            name: 'info',
            internalType: 'struct BitMillions.CharityInfo',
            type: 'tuple',
            components: [
              { name: 'name', internalType: 'string', type: 'string' },
              { name: 'website', internalType: 'string', type: 'string' },
            ],
          },
          {
            name: 'node',
            internalType: 'struct BitMillions.Node',
            type: 'tuple',
            components: [
              { name: 'active', internalType: 'bool', type: 'bool' },
              { name: 'index', internalType: 'uint256', type: 'uint256' },
              { name: 'next', internalType: 'address', type: 'address' },
              { name: 'previous', internalType: 'address', type: 'address' },
            ],
          },
        ],
      },
      {
        name: 'proceeds',
        internalType: 'struct BitMillions.Proceeds',
        type: 'tuple',
        components: [
          { name: 'prizes', internalType: 'uint256', type: 'uint256' },
          { name: 'donations', internalType: 'uint256', type: 'uint256' },
          { name: 'royalties', internalType: 'uint256', type: 'uint256' },
        ],
      },
      {
        name: 'tierWinners',
        internalType: 'struct BitMillions.TierWinners[]',
        type: 'tuple[]',
        components: [
          {
            name: 'tier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'winners', internalType: 'uint256', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getUpkeepConfig',
    outputs: [
      {
        name: '',
        internalType: 'struct BitMillions.UpkeepConfig',
        type: 'tuple',
        components: [
          { name: 'period', internalType: 'uint256', type: 'uint256' },
          { name: 'triggers', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'userAddress', internalType: 'address', type: 'address' }],
    name: 'getUser',
    outputs: [
      {
        name: 'player',
        internalType: 'struct BitMillions.Player',
        type: 'tuple',
        components: [
          { name: 'unclaimedPrizes', internalType: 'uint256', type: 'uint256' },
          { name: 'points', internalType: 'uint256', type: 'uint256' },
          { name: 'lastDrawUpdate', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'lastDrawPrize', internalType: 'uint256', type: 'uint256' },
      {
        name: 'tickets',
        internalType: 'struct BitMillions.Ticket[]',
        type: 'tuple[]',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
      {
        name: 'charity',
        internalType: 'struct BitMillions.Charity',
        type: 'tuple',
        components: [
          { name: 'address_', internalType: 'address', type: 'address' },
          {
            name: 'unclaimedDonations',
            internalType: 'uint256',
            type: 'uint256',
          },
          {
            name: 'info',
            internalType: 'struct BitMillions.CharityInfo',
            type: 'tuple',
            components: [
              { name: 'name', internalType: 'string', type: 'string' },
              { name: 'website', internalType: 'string', type: 'string' },
            ],
          },
          {
            name: 'node',
            internalType: 'struct BitMillions.Node',
            type: 'tuple',
            components: [
              { name: 'active', internalType: 'bool', type: 'bool' },
              { name: 'index', internalType: 'uint256', type: 'uint256' },
              { name: 'next', internalType: 'address', type: 'address' },
              { name: 'previous', internalType: 'address', type: 'address' },
            ],
          },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getVRFConfig',
    outputs: [
      {
        name: '',
        internalType: 'struct BitMillions.VRFConfig',
        type: 'tuple',
        components: [
          { name: 'subId', internalType: 'uint256', type: 'uint256' },
          { name: 'keyHash', internalType: 'bytes32', type: 'bytes32' },
          { name: 'callbackGasLimit', internalType: 'uint32', type: 'uint32' },
          {
            name: 'requestConfirmations',
            internalType: 'uint16',
            type: 'uint16',
          },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'owner',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: '', internalType: 'bytes', type: 'bytes' }],
    name: 'performUpkeep',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'requestId', internalType: 'uint256', type: 'uint256' },
      { name: 'randomWords', internalType: 'uint256[]', type: 'uint256[]' },
    ],
    name: 'rawFulfillRandomWords',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'charityAddress', internalType: 'address', type: 'address' },
    ],
    name: 'removeCharity',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'charityAddress', internalType: 'address', type: 'address' },
    ],
    name: 's_charities',
    outputs: [
      { name: 'address_', internalType: 'address', type: 'address' },
      { name: 'unclaimedDonations', internalType: 'uint256', type: 'uint256' },
      {
        name: 'info',
        internalType: 'struct BitMillions.CharityInfo',
        type: 'tuple',
        components: [
          { name: 'name', internalType: 'string', type: 'string' },
          { name: 'website', internalType: 'string', type: 'string' },
        ],
      },
      {
        name: 'node',
        internalType: 'struct BitMillions.Node',
        type: 'tuple',
        components: [
          { name: 'active', internalType: 'bool', type: 'bool' },
          { name: 'index', internalType: 'uint256', type: 'uint256' },
          { name: 'next', internalType: 'address', type: 'address' },
          { name: 'previous', internalType: 'address', type: 'address' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 's_charityAddresses',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_drawNumber',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'drawNumber', internalType: 'uint256', type: 'uint256' }],
    name: 's_draws',
    outputs: [
      {
        name: 'config',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'jackpot', internalType: 'uint256', type: 'uint256' },
      { name: 'charityAddress', internalType: 'address', type: 'address' },
      {
        name: 'proceeds',
        internalType: 'struct BitMillions.Proceeds',
        type: 'tuple',
        components: [
          { name: 'prizes', internalType: 'uint256', type: 'uint256' },
          { name: 'donations', internalType: 'uint256', type: 'uint256' },
          { name: 'royalties', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'requestId', internalType: 'uint256', type: 'uint256' },
      { name: 'seed', internalType: 'uint256', type: 'uint256' },
      {
        name: 'ticket',
        internalType: 'struct BitMillions.Ticket',
        type: 'tuple',
        components: [
          { name: 'mainNumbers', internalType: 'uint256[]', type: 'uint256[]' },
          { name: 'luckyStars', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_lastUpkeep',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_lockedBalance',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_lotteryState',
    outputs: [
      {
        name: '',
        internalType: 'enum BitMillions.LotteryState',
        type: 'uint8',
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_nextCharityAddress',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_nextDrawConfig',
    outputs: [
      {
        name: 'pools',
        internalType: 'struct BitMillions.Set',
        type: 'tuple',
        components: [
          { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
          { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
        ],
      },
      {
        name: 'jackpotTier',
        internalType: 'struct BitMillions.Set',
        type: 'tuple',
        components: [
          { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
          { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
        ],
      },
      {
        name: 'proceedsAllocation',
        internalType: 'struct BitMillions.Proceeds',
        type: 'tuple',
        components: [
          { name: 'prizes', internalType: 'uint256', type: 'uint256' },
          { name: 'donations', internalType: 'uint256', type: 'uint256' },
          { name: 'royalties', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
      { name: 'ticketTotalLimit', internalType: 'uint256', type: 'uint256' },
      { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'playerAddress', internalType: 'address', type: 'address' },
    ],
    name: 's_players',
    outputs: [
      { name: 'unclaimedPrizes', internalType: 'uint256', type: 'uint256' },
      { name: 'points', internalType: 'uint256', type: 'uint256' },
      { name: 'lastDrawUpdate', internalType: 'uint256', type: 'uint256' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_unclaimedRoyalties',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_upkeepConfig',
    outputs: [{ name: 'period', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_vrfConfig',
    outputs: [
      { name: 'subId', internalType: 'uint256', type: 'uint256' },
      { name: 'keyHash', internalType: 'bytes32', type: 'bytes32' },
      { name: 'callbackGasLimit', internalType: 'uint32', type: 'uint32' },
      { name: 'requestConfirmations', internalType: 'uint16', type: 'uint16' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 's_vrfCoordinator',
    outputs: [
      {
        name: '',
        internalType: 'contract IVRFCoordinatorV2Plus',
        type: 'address',
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: '_vrfCoordinator', internalType: 'address', type: 'address' },
    ],
    name: 'setCoordinator',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'nextDrawConfig',
        internalType: 'struct BitMillions.DrawConfig',
        type: 'tuple',
        components: [
          {
            name: 'pools',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'jackpotTier',
            internalType: 'struct BitMillions.Set',
            type: 'tuple',
            components: [
              { name: 'mainNumbers', internalType: 'uint256', type: 'uint256' },
              { name: 'luckyStars', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'tierAllocations',
            internalType: 'struct BitMillions.TierAllocation[]',
            type: 'tuple[]',
            components: [
              {
                name: 'tier',
                internalType: 'struct BitMillions.Set',
                type: 'tuple',
                components: [
                  {
                    name: 'mainNumbers',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                  {
                    name: 'luckyStars',
                    internalType: 'uint256',
                    type: 'uint256',
                  },
                ],
              },
              { name: 'cut', internalType: 'uint256', type: 'uint256' },
            ],
          },
          {
            name: 'proceedsAllocation',
            internalType: 'struct BitMillions.Proceeds',
            type: 'tuple',
            components: [
              { name: 'prizes', internalType: 'uint256', type: 'uint256' },
              { name: 'donations', internalType: 'uint256', type: 'uint256' },
              { name: 'royalties', internalType: 'uint256', type: 'uint256' },
            ],
          },
          { name: 'ticketPrice', internalType: 'uint256', type: 'uint256' },
          {
            name: 'ticketTotalLimit',
            internalType: 'uint256',
            type: 'uint256',
          },
          { name: 'ticketBuyLimit', internalType: 'uint256', type: 'uint256' },
        ],
      },
    ],
    name: 'setNextDrawConfig',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'upkeepConfig',
        internalType: 'struct BitMillions.UpkeepConfig',
        type: 'tuple',
        components: [
          { name: 'period', internalType: 'uint256', type: 'uint256' },
          { name: 'triggers', internalType: 'uint256[]', type: 'uint256[]' },
        ],
      },
    ],
    name: 'setUpkeepConfig',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'vrfConfig',
        internalType: 'struct BitMillions.VRFConfig',
        type: 'tuple',
        components: [
          { name: 'subId', internalType: 'uint256', type: 'uint256' },
          { name: 'keyHash', internalType: 'bytes32', type: 'bytes32' },
          { name: 'callbackGasLimit', internalType: 'uint32', type: 'uint32' },
          {
            name: 'requestConfirmations',
            internalType: 'uint16',
            type: 'uint16',
          },
        ],
      },
    ],
    name: 'setVRFConfig',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: 'to', internalType: 'address', type: 'address' }],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'charityAddress', internalType: 'address', type: 'address' },
      {
        name: 'charityInfo',
        internalType: 'struct BitMillions.CharityInfo',
        type: 'tuple',
        components: [
          { name: 'name', internalType: 'string', type: 'string' },
          { name: 'website', internalType: 'string', type: 'string' },
        ],
      },
    ],
    name: 'updateCharityInfo',
    outputs: [],
    stateMutability: 'nonpayable',
  },
] as const

/**
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const bitMillionsAddress = {
  8453: '0x0000000000000000000000000000000000000000',
  84532: '0xEc0FE438Ea04abA820d989bcf1476F605212f761',
} as const

/**
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const bitMillionsConfig = {
  address: bitMillionsAddress,
  abi: bitMillionsAbi,
} as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// USDC
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const usdcAbi = [
  {
    type: 'event',
    inputs: [
      { name: 'owner', type: 'address', indexed: true },
      { name: 'spender', type: 'address', indexed: true },
      { name: 'value', type: 'uint256', indexed: false },
    ],
    name: 'Approval',
  },
  {
    type: 'event',
    inputs: [
      { name: 'from', type: 'address', indexed: true },
      { name: 'to', type: 'address', indexed: true },
      { name: 'value', type: 'uint256', indexed: false },
    ],
    name: 'Transfer',
  },
  {
    type: 'function',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'decimals',
    outputs: [{ type: 'uint8' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'name',
    outputs: [{ type: 'string' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'symbol',
    outputs: [{ type: 'string' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'totalSupply',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'sender', type: 'address' },
      { name: 'recipient', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'transferFrom',
    outputs: [{ type: 'bool' }],
    stateMutability: 'nonpayable',
  },
] as const

/**
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const usdcAddress = {
  8453: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  84532: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
} as const

/**
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const usdcConfig = { address: usdcAddress, abi: usdcAbi } as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// React
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillions = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_RANDOM"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsDevRandom = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'DEV_RANDOM',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_TESTING"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsDevTesting = /*#__PURE__*/ createUseReadContract(
  {
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_TESTING',
  },
)

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"LUCKY_STARS_JACKPOT_MARGIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsLuckyStarsJackpotMargin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'LUCKY_STARS_JACKPOT_MARGIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"LUCKY_STARS_POOL_MIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsLuckyStarsPoolMin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'LUCKY_STARS_POOL_MIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"MAIN_NUMBERS_JACKPOT_MARGIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsMainNumbersJackpotMargin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'MAIN_NUMBERS_JACKPOT_MARGIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"MAIN_NUMBERS_POOL_MIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsMainNumbersPoolMin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'MAIN_NUMBERS_POOL_MIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"PROCEEDS_ALLOCATION_DONATIONS_MAX"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsProceedsAllocationDonationsMax =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'PROCEEDS_ALLOCATION_DONATIONS_MAX',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"PROCEEDS_ALLOCATION_DONATIONS_MIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsProceedsAllocationDonationsMin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'PROCEEDS_ALLOCATION_DONATIONS_MIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"PROCEEDS_ALLOCATION_PRIZES_MIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsProceedsAllocationPrizesMin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'PROCEEDS_ALLOCATION_PRIZES_MIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"PROCEEDS_ALLOCATION_ROLLOVER_MIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsProceedsAllocationRolloverMin =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'PROCEEDS_ALLOCATION_ROLLOVER_MIN',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"PROCEEDS_ALLOCATION_ROYALTIES_MAX"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsProceedsAllocationRoyaltiesMax =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'PROCEEDS_ALLOCATION_ROYALTIES_MAX',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"TIERS_MIN"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsTiersMin = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'TIERS_MIN',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"TIMELOCK"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsTimelock = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'TIMELOCK',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"USDC"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsUsdc = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'USDC',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"checkUpkeep"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsCheckUpkeep =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'checkUpkeep',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getCurrentDraw"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetCurrentDraw =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'getCurrentDraw',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getLottery"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetLottery = /*#__PURE__*/ createUseReadContract(
  {
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'getLottery',
  },
)

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getNextDrawConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetNextDrawConfig =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'getNextDrawConfig',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getPlayer"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetPlayer = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'getPlayer',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getPreviousDraw"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetPreviousDraw =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'getPreviousDraw',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getUpkeepConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetUpkeepConfig =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'getUpkeepConfig',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getUser"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetUser = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'getUser',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"getVRFConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsGetVrfConfig =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'getVRFConfig',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"owner"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsOwner = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 'owner',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_charities"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSCharities = /*#__PURE__*/ createUseReadContract(
  {
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_charities',
  },
)

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_charityAddresses"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSCharityAddresses =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_charityAddresses',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_drawNumber"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSDrawNumber =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_drawNumber',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_draws"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSDraws = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 's_draws',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_lastUpkeep"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSLastUpkeep =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_lastUpkeep',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_lockedBalance"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSLockedBalance =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_lockedBalance',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_lotteryState"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSLotteryState =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_lotteryState',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_nextCharityAddress"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSNextCharityAddress =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_nextCharityAddress',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_nextDrawConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSNextDrawConfig =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_nextDrawConfig',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_players"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSPlayers = /*#__PURE__*/ createUseReadContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
  functionName: 's_players',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_unclaimedRoyalties"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSUnclaimedRoyalties =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_unclaimedRoyalties',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_upkeepConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSUpkeepConfig =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_upkeepConfig',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_vrfConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSVrfConfig = /*#__PURE__*/ createUseReadContract(
  {
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_vrfConfig',
  },
)

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"s_vrfCoordinator"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useReadBitMillionsSVrfCoordinator =
  /*#__PURE__*/ createUseReadContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 's_vrfCoordinator',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillions = /*#__PURE__*/ createUseWriteContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_DESTROY"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsDevDestroy =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_DESTROY',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_SET_RANDOM"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsDevSetRandom =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_SET_RANDOM',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_TESTING_fulfillRandomWords"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsDevTestingFulfillRandomWords =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_TESTING_fulfillRandomWords',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"acceptOwnership"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsAcceptOwnership =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'acceptOwnership',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"addCharity"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsAddCharity =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'addCharity',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"buyTickets"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsBuyTickets =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'buyTickets',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"claimDonations"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsClaimDonations =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'claimDonations',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"claimPrizes"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsClaimPrizes =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'claimPrizes',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"claimRoyalties"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsClaimRoyalties =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'claimRoyalties',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"performUpkeep"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsPerformUpkeep =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'performUpkeep',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"rawFulfillRandomWords"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsRawFulfillRandomWords =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'rawFulfillRandomWords',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"removeCharity"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsRemoveCharity =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'removeCharity',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setCoordinator"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsSetCoordinator =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setCoordinator',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setNextDrawConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsSetNextDrawConfig =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setNextDrawConfig',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setUpkeepConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsSetUpkeepConfig =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setUpkeepConfig',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setVRFConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsSetVrfConfig =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setVRFConfig',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"transferOwnership"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsTransferOwnership =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'transferOwnership',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"updateCharityInfo"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWriteBitMillionsUpdateCharityInfo =
  /*#__PURE__*/ createUseWriteContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'updateCharityInfo',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillions = /*#__PURE__*/ createUseSimulateContract({
  abi: bitMillionsAbi,
  address: bitMillionsAddress,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_DESTROY"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsDevDestroy =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_DESTROY',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_SET_RANDOM"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsDevSetRandom =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_SET_RANDOM',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"DEV_TESTING_fulfillRandomWords"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsDevTestingFulfillRandomWords =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'DEV_TESTING_fulfillRandomWords',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"acceptOwnership"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsAcceptOwnership =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'acceptOwnership',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"addCharity"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsAddCharity =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'addCharity',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"buyTickets"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsBuyTickets =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'buyTickets',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"claimDonations"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsClaimDonations =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'claimDonations',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"claimPrizes"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsClaimPrizes =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'claimPrizes',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"claimRoyalties"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsClaimRoyalties =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'claimRoyalties',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"performUpkeep"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsPerformUpkeep =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'performUpkeep',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"rawFulfillRandomWords"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsRawFulfillRandomWords =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'rawFulfillRandomWords',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"removeCharity"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsRemoveCharity =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'removeCharity',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setCoordinator"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsSetCoordinator =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setCoordinator',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setNextDrawConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsSetNextDrawConfig =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setNextDrawConfig',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setUpkeepConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsSetUpkeepConfig =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setUpkeepConfig',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"setVRFConfig"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsSetVrfConfig =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'setVRFConfig',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"transferOwnership"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsTransferOwnership =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'transferOwnership',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link bitMillionsAbi}__ and `functionName` set to `"updateCharityInfo"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useSimulateBitMillionsUpdateCharityInfo =
  /*#__PURE__*/ createUseSimulateContract({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    functionName: 'updateCharityInfo',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"CharityAdded"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsCharityAddedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'CharityAdded',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"CharityInfoUpdated"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsCharityInfoUpdatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'CharityInfoUpdated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"CharityRemoved"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsCharityRemovedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'CharityRemoved',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"CoordinatorSet"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsCoordinatorSetEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'CoordinatorSet',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"Deployed"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDeployedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'Deployed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DiscountApplied"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDiscountAppliedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DiscountApplied',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DonationsClaimed"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDonationsClaimedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DonationsClaimed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DrawClosed"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDrawClosedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DrawClosed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DrawOpened"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDrawOpenedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DrawOpened',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DrawPostponed"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDrawPostponedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DrawPostponed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DrawPrizesCalculated"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDrawPrizesCalculatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DrawPrizesCalculated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"DrawUpdated"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsDrawUpdatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'DrawUpdated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"FundsReceived"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsFundsReceivedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'FundsReceived',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"LastUpkeepUpdated"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsLastUpkeepUpdatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'LastUpkeepUpdated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"LotteryStateChanged"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsLotteryStateChangedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'LotteryStateChanged',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"NextDrawConfigSet"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsNextDrawConfigSetEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'NextDrawConfigSet',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"OwnershipTransferRequested"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsOwnershipTransferRequestedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'OwnershipTransferRequested',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"OwnershipTransferred"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsOwnershipTransferredEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'OwnershipTransferred',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"PlayerReferred"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsPlayerReferredEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'PlayerReferred',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"PrizesClaimed"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsPrizesClaimedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'PrizesClaimed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"RoyaltiesClaimed"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsRoyaltiesClaimedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'RoyaltiesClaimed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"TicketsBought"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsTicketsBoughtEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'TicketsBought',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"UpkeepConfigSet"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsUpkeepConfigSetEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'UpkeepConfigSet',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link bitMillionsAbi}__ and `eventName` set to `"VRFConfigSet"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x0000000000000000000000000000000000000000)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0xEc0FE438Ea04abA820d989bcf1476F605212f761)
 */
export const useWatchBitMillionsVrfConfigSetEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: bitMillionsAbi,
    address: bitMillionsAddress,
    eventName: 'VRFConfigSet',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdc = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"allowance"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdcAllowance = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'allowance',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"balanceOf"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdcBalanceOf = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'balanceOf',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"decimals"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdcDecimals = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'decimals',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"name"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdcName = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'name',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"symbol"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdcSymbol = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'symbol',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"totalSupply"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useReadUsdcTotalSupply = /*#__PURE__*/ createUseReadContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'totalSupply',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link usdcAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWriteUsdc = /*#__PURE__*/ createUseWriteContract({
  abi: usdcAbi,
  address: usdcAddress,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"approve"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWriteUsdcApprove = /*#__PURE__*/ createUseWriteContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'approve',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"transfer"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWriteUsdcTransfer = /*#__PURE__*/ createUseWriteContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'transfer',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"transferFrom"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWriteUsdcTransferFrom = /*#__PURE__*/ createUseWriteContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'transferFrom',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link usdcAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useSimulateUsdc = /*#__PURE__*/ createUseSimulateContract({
  abi: usdcAbi,
  address: usdcAddress,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"approve"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useSimulateUsdcApprove = /*#__PURE__*/ createUseSimulateContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'approve',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"transfer"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useSimulateUsdcTransfer = /*#__PURE__*/ createUseSimulateContract({
  abi: usdcAbi,
  address: usdcAddress,
  functionName: 'transfer',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link usdcAbi}__ and `functionName` set to `"transferFrom"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useSimulateUsdcTransferFrom =
  /*#__PURE__*/ createUseSimulateContract({
    abi: usdcAbi,
    address: usdcAddress,
    functionName: 'transferFrom',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link usdcAbi}__
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWatchUsdcEvent = /*#__PURE__*/ createUseWatchContractEvent({
  abi: usdcAbi,
  address: usdcAddress,
})

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link usdcAbi}__ and `eventName` set to `"Approval"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWatchUsdcApprovalEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: usdcAbi,
    address: usdcAddress,
    eventName: 'Approval',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link usdcAbi}__ and `eventName` set to `"Transfer"`
 *
 * - [__View Contract on Base Basescan__](https://basescan.org/address/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913)
 * - [__View Contract on Base Sepolia Basescan__](https://sepolia.basescan.org/address/0x036CbD53842c5426634e7929541eC2318f3dCF7e)
 */
export const useWatchUsdcTransferEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: usdcAbi,
    address: usdcAddress,
    eventName: 'Transfer',
  })
